import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { LogIn } from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import EmailCodeSignIn from '@/components/EmailCodeSignIn'
import { authApi } from '@/api/client'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { getDesktopBridge } from '@/lib/desktop'

export default function LoginPage() {
  const { data: user, isLoading } = useCurrentUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const desktop = getDesktopBridge()

  useEffect(() => {
    if (!isLoading && user) navigate('/', { replace: true })
  }, [isLoading, user, navigate])

  async function handleSignedIn() {
    // Refetch /api/me before leaving so the Navbar shows the signed-in state
    // on the very first render of the next page.
    await queryClient.invalidateQueries({ queryKey: ['current-user'] })
    navigate('/', { replace: true })
  }

  return (
    <PageTransition>
      <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md"
        >
          <GlassCard className="p-8">
            <div className="flex flex-col items-center text-center">
              <div className="rounded-full bg-accent/10 p-3 mb-4">
                <LogIn size={22} className="text-accent" />
              </div>
              <h1 className="text-xl font-semibold text-[var(--text-primary)] mb-1">
                Sign in to Lens
              </h1>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                Audits and reports are tied to your account.
              </p>

              <EmailCodeSignIn onSignedIn={handleSignedIn} />

              {desktop === null ? (
                <>
                  <div className="w-full flex items-center gap-3 my-5" aria-hidden>
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-xs text-[var(--text-secondary)]">or</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>

                  <a
                    href={authApi.googleSignInUrl()}
                    className="w-full inline-flex items-center justify-center gap-3 px-4 py-2.5 rounded-md
                               bg-white text-slate-900 font-medium text-sm
                               hover:bg-slate-100 transition-colors"
                  >
                    <GoogleGlyph />
                    Sign in with Google
                  </a>

                  <p className="text-xs text-[var(--text-secondary)] mt-6">
                    Either way lands on the same account for the same email address.
                    Google sign-in only reads your email and profile name.
                  </p>
                </>
              ) : (
                <p className="text-xs text-[var(--text-secondary)] mt-5">
                  Google sign-in isn't available in the desktop app — use your email; it's the same account.
                </p>
              )}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </PageTransition>
  )
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
    </svg>
  )
}
